import posthog from 'posthog-js'

import { getAnalyticsEnvironment, type AnalyticsEnvironment } from './appEnvironment'
import { hasAnalyticsConsent } from './analyticsConsent'

let isInitialized = false

const EU_APP_HOST = 'https://eu.posthog.com'

export type PostHogPersonProperties = {
  plan?: string
  signup_source?: string
  environment?: string
}

export type { AnalyticsEnvironment }
export { getAnalyticsEnvironment }

function withEnvironment(properties?: Record<string, unknown>): Record<string, unknown> {
  return {
    environment: getAnalyticsEnvironment(),
    ...properties,
  }
}

function isPostHogClientEnabled(): boolean {
  if (import.meta.env.VITE_POSTHOG_ENABLED !== 'true') {
    return false
  }
  return Boolean(import.meta.env.VITE_POSTHOG_KEY)
}

function ensurePostHogInitialized(): boolean {
  if (isInitialized) {
    return true
  }
  initPostHog()
  return isInitialized
}

export function initPostHog() {
  if (isInitialized || typeof window === 'undefined') {
    return
  }

  if (!isPostHogClientEnabled() || !hasAnalyticsConsent()) {
    return
  }

  const apiKey = import.meta.env.VITE_POSTHOG_KEY
  if (!apiKey) {
    return
  }

  // Same-origin reverse proxy (functions/ph) so ad blockers do not drop analytics.
  // ui_host keeps toolbar/links pointing at the real PostHog app.
  const apiHost = `${window.location.origin}/ph`

  const disableReplay = import.meta.env.VITE_POSTHOG_DISABLE_REPLAY === 'true'

  posthog.init(apiKey, {
    api_host: apiHost,
    ui_host: EU_APP_HOST,
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    ...(disableReplay
      ? {}
      : {
          session_recording: {
            maskAllInputs: true,
          },
        }),
    loaded: (client) => {
      client.register({ environment: getAnalyticsEnvironment() })
      if (import.meta.env.MODE === 'development') {
        client.debug(false)
      }
    },
  })

  isInitialized = true
}

export function capturePostHogPageView(path: string) {
  if (!ensurePostHogInitialized()) {
    return
  }
  posthog.capture(
    '$pageview',
    withEnvironment({
      $current_url: path.startsWith('http') ? path : `${window.location.origin}${path}`,
    }),
  )
}

export function capturePostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
) {
  if (!ensurePostHogInitialized()) {
    return
  }
  posthog.capture(event, withEnvironment(properties))
}

export function identifyPostHogUser(
  userId: string,
  properties?: PostHogPersonProperties,
) {
  if (!userId || !ensurePostHogInitialized()) {
    return
  }
  posthog.identify(userId, {
    ...properties,
    environment: properties?.environment ?? getAnalyticsEnvironment(),
  })
}

export function resetPostHogUser() {
  if (!isInitialized) {
    return
  }
  posthog.reset()
}

export function isPostHogInitialized() {
  return isInitialized
}

export function isPostHogEnabled() {
  return isPostHogClientEnabled()
}

export { posthog }
